import Zdog from 'zdog';

import {
  PIN_COLOR_ACTIVE,
  DEFAULT_GRID_COLOR,
  PIN_COLOR_INACTIVE,
  mockFloorPath,
  mockZones,
  PIN_MARKER_PATH,
  DEFAULT_ZONE_COLOR,
  DEFAULT_FLOOR_HEIGHT,
} from 'consts';
import { EAxis, EZoneStatus } from 'enums';
import {
  IBackgroundGrids,
  IBuilding,
  IBuildingVisualization,
  IFloorVisualization,
  IPinScene,
  IPosition2d,
  IPosition3d,
  IRect,
  IZone,
  IZoneVisualization,
} from 'models';

import { getBuildingImageUrl, getBuildingLocation } from '.';

const generateAxisLines = (payload: {
  axis1: EAxis;
  axis2: EAxis;
  offset?: number;
  lineGroup: Zdog.Group;
  gridColor?: string;
}) => {
  const { axis1, axis2, offset = 20, lineGroup, gridColor = DEFAULT_GRID_COLOR } = payload;

  for (let i = 0; i < 9999; i += offset) {
    new Zdog.Shape({
      addTo: lineGroup,
      path: [
        { [axis1]: 0, [axis2]: i },
        { [axis1]: 9999, [axis2]: i },
      ],
      stroke: 1,
      color: gridColor,
    });

    new Zdog.Shape({
      addTo: lineGroup,
      path: [
        { [axis2]: 0, [axis1]: i },
        { [axis2]: 9999, [axis1]: i },
      ],
      stroke: 1,
      color: gridColor,
    });
  }
};

const generateBackgroundScene = (scene: Zdog.Anchor, axisOffset: IPosition3d) => {
  const xy = new Zdog.Group({
    addTo: scene,
  });

  const yz = new Zdog.Group({
    addTo: scene,
  });

  const zx = new Zdog.Group({
    addTo: scene,
  });

  generateAxisLines({ axis1: EAxis.X, axis2: EAxis.Y, lineGroup: xy });
  generateAxisLines({ axis1: EAxis.Y, axis2: EAxis.Z, lineGroup: yz });
  generateAxisLines({ axis1: EAxis.Z, axis2: EAxis.X, lineGroup: zx });

  const { x, y, z } = axisOffset;

  xy.translate.x = yz.translate.x = zx.translate.x = x;
  xy.translate.y = yz.translate.y = zx.translate.y = y;
  xy.translate.z = yz.translate.z = zx.translate.z = z;

  return {
    xy,
    yz,
    zx,
  } as IBackgroundGrids;
};

const generatePinSceneSeed = (color: string) => {
  const scene = new Zdog.Anchor();

  const pinPathGroup = new Zdog.Group({
    addTo: scene,
  });

  new Zdog.Shape({
    translate: { x: -8.5, y: -8.5, z: 0 },
    addTo: pinPathGroup,
    path: PIN_MARKER_PATH,
    closed: true,
    stroke: 0,
    fill: true,
    color,
  });

  new Zdog.Ellipse({
    translate: { x: 0, y: 0, z: 0 },
    addTo: pinPathGroup,
    diameter: 12,
    stroke: 0,
    fill: true,
    color: 'white',
  });

  new Zdog.Ellipse({
    translate: { x: 0, y: 0, z: 0 },
    addTo: pinPathGroup,
    diameter: 7,
    stroke: 0,
    fill: true,
    color,
  });

  return { scene, pinPathGroup };
};

const generatePinScene = (payload: { position: { x: number; y: number; z: number }; color?: string }) => {
  const { position, color = PIN_COLOR_ACTIVE } = payload;
  const { scene, pinPathGroup } = generatePinSceneSeed(color);

  scene.translate.x = position.x;
  scene.translate.y = position.y;
  scene.translate.z = position.z;

  return {
    scene,
    position,
    pinPathGroup,
  };
};

export const generateScenes = (payload: { floors: Map<number, IFloorVisualization>; floorHeight?: number }) => {
  const { floors, floorHeight = DEFAULT_FLOOR_HEIGHT } = payload;
  const buildingScene = new Zdog.Anchor();
  const pinScenes: Array<IPinScene> = [];

  const floorCount = floors.size;
  const mid = ((floorCount - 1) * floorHeight) / 2;

  Array.from(floors.keys()).forEach((floorId, floorIndex) => {
    const floor = floors.get(floorId);

    if ((floor?.zones.size || 0) > 0) {
      Array.from(floor?.zones.keys() || []).forEach((zoneId) => {
        const zone = floor?.zones.get(zoneId);

        if (zone) {
          zone.pathShape = new Zdog.Shape({
            addTo: buildingScene,
            path: zone.path,
            translate: { z: floorHeight * floorIndex - mid },
            color: DEFAULT_ZONE_COLOR,
            stroke: 0.5,
            fill: true,
          });

          if (zone.status !== EZoneStatus.PENDING) {
            const pinScene = generatePinScene({
              position: {
                x: zone.pinPosition.x,
                y: zone.pinPosition.y,
                z: floorHeight * floorIndex - mid + 20,
              },
              color: zone.status === EZoneStatus.ACTIVE ? undefined : PIN_COLOR_INACTIVE,
            });

            zone.pinPathGroup = pinScene.pinPathGroup;

            pinScenes.push({
              scene: pinScene.scene,
              position: pinScene.position,
              floorId,
              zoneId,
            });
          }
        }
      });
    } else {
      new Zdog.Shape({
        addTo: buildingScene,
        path: mockFloorPath,
        translate: { z: floorHeight * floorIndex - mid },
        color: DEFAULT_ZONE_COLOR,
        stroke: 0.5,
        fill: true,
      });
    }
  });

  // This must be calculated depending on the actual building size
  const axisOffset = {
    x: 240,
    y: 180,
    z: mid + 60,
  };

  const backgroundGrids = generateBackgroundScene(buildingScene, axisOffset);

  buildingScene.rotate.x += 1.35;
  buildingScene.rotate.z += 0.8;

  return {
    buildingScene,
    pinScenes,
    backgroundGrids,
    axisOffset,
  };
};

export const initVisualization = (payload: { building?: IBuilding; zones: Array<IZone> }) => {
  const { building, zones } = payload;

  if (!building) {
    return null;
  }

  const buildingInfo = {
    id: building.id,
    thumbnail: getBuildingImageUrl(building),
    location: getBuildingLocation(building),
    floorsCount: building.floors.length,
    totalZonesCount: zones.length,
    activeZonesCount: zones.filter((zone) => zone.status === EZoneStatus.ACTIVE).length,
    pendingZonesCount: zones.filter((zone) => zone.status === EZoneStatus.PENDING).length,
    inactiveZonesCount: zones.filter((zone) => zone.status === EZoneStatus.INACTIVE).length,
    expanded: true,
  } as IBuildingVisualization;

  const floors = new Map<number, IFloorVisualization>();

  for (let i = 0; i < building.floors.length; i++) {
    const floor = building.floors[i];
    const floorIndex = i;

    const floorInfo = {
      id: floor.id,
      floorIndex,
      name: floor.floor,
      zones: new Map<number, IZoneVisualization>(),
      expanded: false,
    } as IFloorVisualization;

    floors.set(floor.id, floorInfo);
  }

  for (let i = 0; i < zones.length; i++) {
    const zoneIndex = i;
    const zone = zones[i];
    const floorId = zone.building_floor_id;
    const floorIndex = floors.get(floorId)?.floorIndex;
    const floorZones = floors.get(floorId)?.zones;

    const { path, pinPosition } = mockZones[i % 3];

    const zoneInfo = {
      id: zone.id,
      zoneIndex,
      floorId,
      floorIndex,
      name: zone.name,
      status: zone.status,
      lastService: zone.logs.length > 0 ? zone.logs[0]?.day_porter?.name : '-',
      rating: zone.rating,
      path,
      pinPosition,
      expanded: false,
      pathShape: new Zdog.Shape(),
      pinPathGroup: new Zdog.Group(),
    } as IZoneVisualization;

    if (floorZones) {
      floorZones.set(zone.id, zoneInfo);
    }
  }

  const { buildingScene, pinScenes, backgroundGrids, axisOffset } = generateScenes({ floors });

  return {
    axisOffset,
    backgroundGrids,
    buildingTree: {
      building: buildingInfo,
      floors,
    },
    buildingScene,
    pinScenes,
  };
};

export const isPointInRect = (rect: IRect, point: IPosition2d) => {
  return point.x > rect.x && point.x < rect.x + rect.offsetX && point.y > rect.y && point.y < rect.y + rect.offsetY;
};
